import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * HoverCount - animated counter that:
 *  • counts up from 0 → `value` when the value first becomes available
 *    AND the element has scrolled into view (whichever happens last),
 *  • re-triggers the count-up animation every time the user hovers, and
 *  • shows a friendly "—" placeholder while the value is still 0 so
 *    we never display a sad "0+" empty state.
 *
 * Used by the landing page "AI-Powered Creation" stats strip
 * (Phase 95, updated Phase 100) to show live platform totals.
 */
function HoverCount({ value = 0, duration = 1600, label, suffix = '+', testid }) {
  const target = Math.max(0, Number(value) || 0);
  // Phase 117 - initial display value seeded with `target` (not 0) so
  // the FIRST paint shows the real number to JS-rendering AI crawlers
  // (Perplexity browse-mode, Googlebot Smartphone) instead of "0+". The
  // count-up animation still fires on scroll-into-view and hover, just
  // from 0 → target each time. Without this seed, every crawler that
  // snapshots before our IntersectionObserver fires sees "0+ Created
  // Storybooks" and reports the platform looks empty - exactly the
  // false-positive Perplexity's Feb-2026 audit called out.
  const [display, setDisplay] = useState(target);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  const rafRef = useRef(null);

  const animate = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (target <= 0) {
      setDisplay(0);
      return;
    }
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic for snappy-but-soft landing
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(target * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, [target, duration]);

  // One-shot intersection observer to mark "we are visible".
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Fire the count-up whenever (a) we're visible AND (b) we have a
  // non-zero target. Re-fires automatically when the value lands later
  // than the IntersectionObserver - fixes the original "stuck at 0+"
  // bug where stats arrived after the strip had already scrolled in.
  useEffect(() => {
    if (inView && target > 0) {
      animate();
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [inView, target, animate]);

  const showSuffix = target > 0;

  // Phase 117.9 - Perplexity AI-search audit feedback: expose the
  // REAL platform total as machine-readable `data-*` attributes on
  // the wrapper. AI engines (Perplexity, GPT-Browse, Claude with
  // Browse) walk the DOM and prefer attribute extraction over
  // OCRing the rendered "1,234+" string. The animated text is still
  // the source of truth for humans; these attributes are the source
  // of truth for crawlers.
  const slug = (label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

  return (
    <div
      ref={ref}
      data-testid={testid}
      data-byb-stat={slug || undefined}
      data-byb-stat-value={target}
      data-byb-stat-label={label}
      onMouseEnter={animate}
      className="rounded-2xl px-4 py-4 text-center cursor-default transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
      style={{
        background: 'linear-gradient(135deg, #FFF7E6 0%, #FFE6CF 100%)',
        border: '1px solid #FDD8B9',
        boxShadow: '0 4px 12px rgba(251, 146, 60, 0.12)',
      }}
    >
      <div
        className="text-3xl sm:text-4xl font-bold tracking-tight"
        style={{ fontFamily: 'Fredoka', color: '#7C2D12', fontVariantNumeric: 'tabular-nums' }}
      >
        {target === 0 ? '—' : `${display.toLocaleString()}${showSuffix ? suffix : ''}`}
      </div>
      <div className="mt-1 text-xs sm:text-sm font-medium uppercase tracking-wide" style={{ color: '#9A3412' }}>
        {label}
      </div>
    </div>
  );
}

export default HoverCount;
